/**
 * PDF Viewer Component
 * Displays reference documents (industry standards) as PDFs
 * Single-page view with navigation controls for optimal performance
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Button, Input } from '@/components/ui';
import { logger } from '../../utils/logger';

// Configure PDF.js worker - use unpkg for reliability
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfViewerProps {
  pdfUrl: string;
  documentTitle: string;
}

export default function PdfViewer({ pdfUrl, documentTitle }: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState<'fit' | number>('fit'); // Default to fit-to-width
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [_pageLoading, setPageLoading] = useState(true);
  const [containerWidth, setContainerWidth] = useState(800);
  
  const containerRef = useRef<HTMLDivElement>(null);

  // Track container width for fit-to-width mode
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      const width = container.clientWidth;
      if (width > 0) {
        setContainerWidth(width);
      }
    };

    // Initial measurement
    updateWidth();

    // Watch for resize
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // NOTE: PDF blob caching removed - we now download directly from S3 URL
  // to avoid any blob processing that might corrupt the PDF for Adobe Acrobat

  // Handle download - download directly from S3 URL to avoid blob corruption
  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    
    try {
      // Fetch PDF as ArrayBuffer to preserve binary integrity
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error('Failed to fetch PDF');
      
      // Use ArrayBuffer (not blob()) to ensure no encoding issues
      const arrayBuffer = await response.arrayBuffer();
      
      // Create blob with explicit PDF MIME type
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      
      // Format filename: DocumentTitle-YYYY-MM-DD-HHMM.pdf
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const timeStr = now.toTimeString().slice(0, 5).replace(':', ''); // HHMM
      const safeTitle = (documentTitle || 'Document')
        .replace(/[^a-zA-Z0-9-_]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, ''); // Remove leading/trailing dashes
      const filename = `${safeTitle || 'Document'}-${dateStr}-${timeStr}.pdf`;
      
      // Force download with properly named file
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL after download starts
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      logger.error('Download failed:', err);
      // Fallback: open in new tab
      window.open(pdfUrl, '_blank');
    } finally {
      setIsDownloading(false);
    }
  }, [pdfUrl, documentTitle]);

  const handleOpenInNewTab = useCallback(() => {
    window.open(pdfUrl, '_blank');
  }, [pdfUrl]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setError(null);
    setPageLoading(false);
  }

  function onDocumentLoadError(error: Error) {
    logger.error('PDF load error:', error);
    setError(error.message);
    setPageLoading(false);
  }

  function onPageLoadSuccess() {
    setPageLoading(false);
  }

  function _onPageLoadStart() {
    setPageLoading(true);
  }

  const goToPage = useCallback((page: number) => {
    if (page < 1 || page > numPages) return;
    setPageNumber(page);
    setPageLoading(true);
  }, [numPages]);

  const goToPrevPage = () => goToPage(pageNumber - 1);
  const goToNextPage = () => goToPage(pageNumber + 1);
  const zoomIn = () => setScale(prev => {
    const current = prev === 'fit' ? 1.0 : prev;
    return Math.min(3.0, current + 0.25);
  });
  const zoomOut = () => setScale(prev => {
    const current = prev === 'fit' ? 1.0 : prev;
    return Math.max(0.5, current - 0.25);
  });
  const fitToWidth = () => setScale('fit');
  const setFixedScale = (value: number) => setScale(value);
  
  // Calculate the width to pass to Page component
  const getPageWidth = () => {
    if (scale === 'fit') {
      // Leave some padding (32px on each side)
      return Math.max(400, containerWidth - 64);
    }
    return undefined; // Use scale instead
  };
  
  const getPageScale = () => {
    if (scale === 'fit') {
      return undefined; // Use width instead
    }
    return scale;
  };
  
  const getDisplayScale = () => {
    if (scale === 'fit') {
      return 'Fit';
    }
    return `${Math.round(scale * 100)}%`;
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold text-foreground mb-2">
            Failed to Load PDF
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {error}
          </p>
          <p className="text-xs text-muted-foreground">
            PDF URL: {pdfUrl}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-muted">
      {/* PDF Controls */}
      <div className="flex-shrink-0 bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Page Navigation */}
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              title="Previous page"
            >
              ←
            </Button>
            <span className="text-sm text-foreground">
              Page {pageNumber} of {numPages || '...'}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              title="Next page"
            >
              →
            </Button>
            <Input
              type="number"
              min={1}
              max={numPages || 1}
              value={pageNumber}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= numPages) {
                  goToPage(page);
                }
              }}
              className="w-16 text-sm"
              title="Jump to page"
            />
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center gap-2">
            <Button
              variant={scale === 'fit' ? 'default' : 'secondary'}
              size="sm"
              onClick={fitToWidth}
              title="Fit to width"
            >
              Fit Width
            </Button>
            <Button
              variant={scale === 1.0 ? 'default' : 'secondary'}
              size="sm"
              onClick={() => setFixedScale(1.0)}
              title="100%"
            >
              100%
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={zoomOut}
              disabled={scale !== 'fit' && scale <= 0.5}
              title="Zoom out"
            >
              −
            </Button>
            <span className="text-sm text-foreground min-w-[60px] text-center">
              {getDisplayScale()}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={zoomIn}
              disabled={scale !== 'fit' && scale >= 3.0}
              title="Zoom in"
            >
              +
            </Button>
          </div>

          {/* Download & Open in New Tab */}
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleOpenInNewTab}
              title="Open PDF in new browser tab"
            >
              ↗ Open
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDownload}
              disabled={isDownloading}
              title="Download PDF to your computer"
            >
              {isDownloading ? (
                <>
                  <span className="animate-spin inline-block mr-1">⏳</span>
                  Downloading...
                </>
              ) : (
                <>⬇ Download</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* PDF Content - Single page view */}
      <div 
        ref={containerRef}
        className="flex-1 overflow-auto bg-muted"
      >
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex items-center justify-center h-full py-12">
              <div className="text-center">
                <div className="relative mb-4">
                  <div className="w-16 h-16 border-4 border-border border-t-purple-600 dark:border-t-purple-400 rounded-full animate-spin mx-auto"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl">📄</span>
                  </div>
                </div>
                <div className="text-lg font-medium text-foreground mb-1">
                  Loading PDF...
                </div>
                <div className="text-sm text-muted-foreground">{documentTitle}</div>
              </div>
            </div>
          }
        >
          {numPages > 0 && (
            <div className="flex flex-col items-center p-4">
              <Page
                key={`page_${pageNumber}_${scale}`}
                pageNumber={pageNumber}
                width={getPageWidth()}
                scale={getPageScale()}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                className="shadow-lg"
                onLoadSuccess={onPageLoadSuccess}
                onRenderSuccess={() => setPageLoading(false)}
                loading={
                  <div 
                    className="flex items-center justify-center bg-card shadow-lg"
                    // eslint-disable-next-line no-restricted-syntax -- Dynamic width/height based on scale
                    style={{ 
                      width: scale === 'fit' ? containerWidth - 64 : 612 * scale, 
                      height: scale === 'fit' ? (containerWidth - 64) * 1.294 : 792 * scale 
                    }}
                  >
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-border border-t-purple-600 rounded-full animate-spin mx-auto mb-2"></div>
                      <div className="text-muted-foreground text-sm">
                        Loading page {pageNumber}...
                      </div>
                    </div>
                  </div>
                }
              />
              <div className="text-center mt-3 text-sm text-muted-foreground">
                Page {pageNumber} of {numPages}
              </div>
            </div>
          )}
        </Document>
      </div>
    </div>
  );
}
